import { Resource } from "react-admin";
import type { SelfClaims } from "./api/authProvider";
import { PhEmotionsList, PhEmotionsShow } from "./resources/phEmotions";
import { PhCatalogCreate, PhCatalogEdit, PhCatalogList } from "./resources/phCatalog";
import { PhAppConfigEdit } from "./resources/phAppConfig";
import { PhBreathingCreate, PhBreathingEdit, PhBreathingList } from "./resources/phBreathing";
import { PhEmotionTypeCreate, PhEmotionTypeEdit, PhEmotionTypeList } from "./resources/phEmotionType";
import { PhHelperCreate, PhHelperEdit } from "./resources/phHelper";
import { PhMapPointCreate, PhMapPointEdit, PhMapPointList, phMapPointIcon } from "./resources/phMapPoint";
import { PhHomeSectionCreate, PhHomeSectionEdit, PhHomeSectionList } from "./resources/phHomeSection";
import { PhIntroCreate, PhIntroEdit, PhIntroList } from "./resources/phIntro";
import { PhMusicCreate, PhMusicEdit, PhMusicList } from "./resources/phMusic";
import { PhNewsCreate, PhNewsEdit, PhNewsList } from "./resources/phNews";
import { PhBlogCreate, PhBlogEdit, PhBlogList } from "./resources/phBlog";
import { PhAboutCreate, PhAboutEdit, PhAboutList } from "./resources/phAbout";
import { PhVideoCreate, PhVideoEdit, PhVideoList } from "./resources/phVideo";
import { PhGameCreate, PhGameEdit, PhGameList } from "./resources/phGame";
import { PhPhraseCreate, PhPhraseEdit, PhPhraseList } from "./resources/phPhrase";
import { ExceptionLogList, SlowRequestLogList } from "./resources/logs";

import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import MoodIcon from "@mui/icons-material/Mood";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";
import PsychologyIcon from "@mui/icons-material/Psychology";
import HomeIcon from "@mui/icons-material/Home";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import ArticleIcon from "@mui/icons-material/Article";
import InfoIcon from "@mui/icons-material/Info";
import VideocamIcon from "@mui/icons-material/Videocam";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SettingsApplicationsIcon from "@mui/icons-material/SettingsApplications";
import BugReportIcon from "@mui/icons-material/BugReport";
import SpeedIcon from "@mui/icons-material/Speed";
import { withBrandIcon } from "./ui/brandIcon";

const isAdmin = (perm?: SelfClaims) =>
  !!perm && (perm.roles.includes("Admin") || perm.roles.includes("Manager"));

export const AdminResources = (permissions?: SelfClaims) => [
  <Resource
    key="phphrase"
    name="phphrase"
    list={PhPhraseList}
    edit={PhPhraseEdit}
    create={isAdmin(permissions) ? PhPhraseCreate : undefined}
    icon={withBrandIcon(FormatQuoteIcon)}
  />,
  <Resource
    key="phmusic"
    name="phmusic"
    list={PhMusicList}
    edit={PhMusicEdit}
    create={isAdmin(permissions) ? PhMusicCreate : undefined}
    icon={withBrandIcon(MusicNoteIcon)}
  />,
  <Resource
    key="phemotions"
    name="phemotions"
    list={PhEmotionsList}
    show={PhEmotionsShow}
    icon={withBrandIcon(MoodIcon)}
    options={{ label: "Записи эмоций" }}
  />,
  <Resource
    key="phintro"
    name="phintro"
    list={PhIntroList}
    edit={PhIntroEdit}
    create={isAdmin(permissions) ? PhIntroCreate : undefined}
    icon={withBrandIcon(OndemandVideoIcon)}
    options={{ label: "Intro-видео" }}
  />,
  <Resource
    key="phbreathing"
    name="phbreathing"
    list={PhBreathingList}
    edit={PhBreathingEdit}
    create={isAdmin(permissions) ? PhBreathingCreate : undefined}
    icon={withBrandIcon(SelfImprovementIcon)}
    options={{ label: "Дыхательные упражнения" }}
  />,
  <Resource
    key="phemotiontype"
    name="phemotiontype"
    list={PhEmotionTypeList}
    edit={PhEmotionTypeEdit}
    create={isAdmin(permissions) ? PhEmotionTypeCreate : undefined}
    icon={withBrandIcon(PsychologyIcon)}
    options={{ label: "Типы эмоций" }}
  />,
  <Resource
    key="phhomesection"
    name="phhomesection"
    list={PhHomeSectionList}
    edit={PhHomeSectionEdit}
    create={isAdmin(permissions) ? PhHomeSectionCreate : undefined}
    icon={withBrandIcon(HomeIcon)}
    options={{ label: "Секции главной" }}
  />,
  <Resource
    key="phhelper"
    name="phhelper"
    edit={PhHelperEdit}
    create={isAdmin(permissions) ? PhHelperCreate : undefined}
    icon={withBrandIcon(SupportAgentIcon)}
    recordRepresentation="TitleAZ"
    options={{ label: "Digər köməkçilər" }}
  />,
  <Resource
    key="phnews"
    name="phnews"
    list={PhNewsList}
    edit={PhNewsEdit}
    create={isAdmin(permissions) ? PhNewsCreate : undefined}
    icon={withBrandIcon(NewspaperIcon)}
    options={{ label: "Новости" }}
  />,
  <Resource
    key="phblog"
    name="phblog"
    list={PhBlogList}
    edit={PhBlogEdit}
    create={isAdmin(permissions) ? PhBlogCreate : undefined}
    icon={withBrandIcon(ArticleIcon)}
    options={{ label: "Блог" }}
  />,
  <Resource
    key="phabout"
    name="phabout"
    list={PhAboutList}
    edit={PhAboutEdit}
    create={isAdmin(permissions) ? PhAboutCreate : undefined}
    icon={withBrandIcon(InfoIcon)}
    options={{ label: "Haqqımızda" }}
  />,
  <Resource
    key="phvideo"
    name="phvideo"
    list={PhVideoList}
    edit={PhVideoEdit}
    create={isAdmin(permissions) ? PhVideoCreate : undefined}
    icon={withBrandIcon(VideocamIcon)}
    options={{ label: "Media" }}
  />,
  <Resource
    key="phgame"
    name="phgame"
    list={PhGameList}
    edit={PhGameEdit}
    create={isAdmin(permissions) ? PhGameCreate : undefined}
    icon={withBrandIcon(SportsEsportsIcon)}
    options={{ label: "Oyunlar" }}
  />,
  <Resource
    key="phcatalog"
    name="phcatalog"
    list={PhCatalogList}
    edit={PhCatalogEdit}
    create={isAdmin(permissions) ? PhCatalogCreate : undefined}
    icon={withBrandIcon(MenuBookIcon)}
    options={{ label: "Tibbi mənbə" }}
  />,
  <Resource
    key="phmappoint"
    name="phmappoint"
    list={PhMapPointList}
    edit={PhMapPointEdit}
    create={isAdmin(permissions) ? PhMapPointCreate : undefined}
    icon={withBrandIcon(phMapPointIcon)}
    recordRepresentation="TitleAZ"
    options={{ label: "Yardım xəritəsi" }}
  />,
  <Resource
    key="phappconfig"
    name="phappconfig"
    edit={PhAppConfigEdit}
    icon={withBrandIcon(SettingsApplicationsIcon)}
    recordRepresentation={() => "App parametrləri"}
    options={{ label: "App parametrləri" }}
  />,
  ...(isAdmin(permissions)
    ? [
        <Resource
          key="exceptionlog"
          name="exceptionlog"
          list={ExceptionLogList}
          icon={withBrandIcon(BugReportIcon)}
          options={{ label: "Логи ошибок" }}
        />,
        <Resource
          key="slowrequestlog"
          name="slowrequestlog"
          list={SlowRequestLogList}
          icon={withBrandIcon(SpeedIcon)}
          options={{ label: "Медленные запросы" }}
        />,
      ]
    : []),
];
